import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import PropTypes from "prop-types"
import { useState } from "react"
import styled from "styled-components"
import Alert from "../alert/Alert"
import Button from "../button/Button"
import LazyCodeEditor from "../form/form-code-editor/LazyCodeEditor"
import TextInput from "../form/form-text-input/TextInput"
import useDebouncedValue from "../hooks/useDebouncedValue"
import useDidMountEffect from "../hooks/useDidMountEffect"
import useLoadingState from "../hooks/useLoadingState"
import EmbedDisplay from "./EmbedDisplay"
import { embedIframelyURL } from "./utils"

export const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  align-items: center;

  .CodeMirror {
    height: auto;
    min-height: 50px;
    border: 1px solid rgba(0, 0, 0, 0.2);
  }

  .UIFieldWrapper.TextInput > .styled-field {
    position: absolute;
    right: 9px;
    top: calc(50% - 9px);
    width: auto;

    .LoadingCircle {
      min-width: 18px !important;
      min-height: 18px !important;
    }
  }
`

const MODE_URL = "url"
const MODE_EMBED = "embed"

const EmbedForm = ({
  url: urlProp,
  embedCode: embedCodeProp,
  onChange,
  renderEmbed
}) => {
  const { t } = useTranslate()
  // NOTE: debouncing the url state slightly
  const [url, debouncedUrl, debouncingUrl, setUrl] = useDebouncedValue(
    300,
    urlProp || ""
  )
  const [embedCode, setEmbedCode] = useState(embedCodeProp || "")
  // defaulting to MODE_URL
  const [mode, setMode] = useState(
    url ? MODE_URL : embedCode ? MODE_EMBED : MODE_URL
  )
  const [error, setError] = useState(null)

  // toggle the current mode
  const toggleMode = () => setMode(mode === MODE_URL ? MODE_EMBED : MODE_URL)

  // wrapping with useLoadingState for loading state
  const [handleEmbedURL, embedLoading] = useLoadingState(async () => {
    // fetching the embed code for the url via iframely
    try {
      const result = await embedIframelyURL(url)

      // url likely valid, but could not embed
      if (!result || !result.html) {
        console.log("No valid embed data found.", result)
        const error = url
          ? t(`No embed data found for "{{ url }}".`, {
              url
            })
          : t("No embed data found.")
        setError(error)
        setEmbedCode("")
      } else {
        // success!
        setEmbedCode(result.html)
        setUrl(result.url)
      }
    } catch (error) {
      console.error(error)
      setError(error)
      setEmbedCode("")
    }
  })

  // reset error when switching to MODE_EMBED
  useDidMountEffect(() => {
    if (mode === MODE_EMBED) setError(null)
  }, [mode])

  // as changes are made to the url and embedCode,
  // send the state upstream
  useDidMountEffect(() => {
    if (onChange) onChange({ url: debouncedUrl, embedCode })
  }, [debouncedUrl, embedCode])

  // handle the url value as it changes, checking for errors,
  // and if no errors, pass on to handleEmbedURL
  useDidMountEffect(() => {
    if (debouncedUrl) {
      try {
        // attempt to construct url
        const urlObj = new URL(debouncedUrl)
        // ensure https
        if (!/^https\:/.test(urlObj.protocol)) {
          setError(
            `Please provide a url that links to a secure resource (it should begin with "https://").`
          )
        } else {
          handleEmbedURL(debouncedUrl)
        }
      } catch (error) {
        setError("Invalid url.")
      }
    } else {
      setError(null)
    }
  }, [debouncedUrl])

  return (
    <Container>
      <div
        style={{
          width: "100%",
          margin: "0 auto"
        }}
      >
        {mode === MODE_URL ? (
          <TextInput
            placeholder={t("Add a URL to embed")}
            value={url}
            onChange={(value) => {
              setError(null)
              // Remove extra white space
              value = value ? value.trim() : ""
              setUrl(value)
            }}
            validating={embedLoading}
          />
        ) : null}
        {mode === MODE_EMBED ? (
          <LazyCodeEditor
            placeholder={t("Paste embed code")}
            value={embedCode}
            onChange={(value) => {
              setError(null)
              setEmbedCode(value)
              // Clear the URL field when manually updating the embed code
              if (debouncedUrl) {
                setUrl("")
              }
            }}
            codeMirrorOptions={{
              theme: "default",
              mode: "htmlmixed",
              lineNumbers: true,
              lineWrapping: true
            }}
          />
        ) : null}
      </div>
      <Button
        styleType="text"
        onClick={toggleMode}
        style={{ marginTop: "1em", marginBottom: "2em" }}
      >
        {mode === MODE_URL
          ? t("Or paste an embed code instead")
          : t("Or paste a URL instead")}
      </Button>
      {error ? (
        <Alert error={error} />
      ) : embedCode ? (
        renderEmbed ? (
          renderEmbed({ embedCode })
        ) : (
          <EmbedDisplay embedCode={embedCode} />
        )
      ) : null}
    </Container>
  )
}

EmbedForm.displayName = "EmbedForm"

EmbedForm.propTypes = {
  url: PropTypes.string,
  embedCode: PropTypes.string,
  onChange: PropTypes.func,
  renderEmbed: PropTypes.func
}

export default EmbedForm
